import { EyeOutlined } from "@ant-design/icons"
import { App, Button, Card, Empty, Flex, Skeleton, Typography } from "antd"
import React, { useEffect, useMemo, useState } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { useNavigate, useParams } from "react-router"
import { Link, Navigate } from "react-router-dom"

import { FixedContainer } from "../../../components/FixedContainer.tsx"
import { excludeDeletedAccounts } from "../../../constants/deleted-account-ui.ts"
import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import type { RootState } from "../../../store.ts"
import {
  projectsViewPath,
  useProjectsView
} from "../../Projects/helpers/projects-view-preference.ts"
import { fetchProjectsFilterOptions } from "../../Projects/redux/projects.thunks.ts"
import { ProjectForm } from "../ProjectAddPage/components/ProjectForm.tsx"
import { type ProjectFormData } from "../ProjectAddPage/components/project-form-data.ts"
import { PROJECT_ENTITY_CATEGORIES } from "../ProjectViewPage/constants/project-entity-category.ts"
import { diffProjectTypeSelection } from "../helpers/diff-project-types.ts"
import { getProjectNavigatePath } from "../helpers/get-project-navigate-path.tsx"
import {
  addProjectTypeToProject,
  fetchProject,
  removeProjectTypeToProject,
  updateProject
} from "../redux/project.thunks.ts"
import { type ProjectUpdateData } from "../types/project.types.ts"

const { Title } = Typography

export const ProjectEditPage = ({
  user,
  project,
  saving,
  filterOptions,
  updateProject,
  addProjectType,
  removeProjectType,
  fetching,
  fetchFilterOptions,
  getProjectById
}: ConnectedProps<typeof connector>) => {
  const { modal } = App.useApp()
  const navigate = useNavigate()
  const [projectsView] = useProjectsView()
  const id = useParams()?.id as string
  const [submitting, setSubmitting] = useState(false)

  const options = useMemo(
    () => ({
      state: filterOptions?.state,
      sales_manager: filterOptions?.sales_manager,
      source: filterOptions?.source,
      contact_method: filterOptions?.contact_method,
      project_manager: filterOptions?.project_manager,
      subcontractor: filterOptions?.subcontractor?.map((s) => ({
        label: s?.label,
        value: s?.value?.id
      }))
    }),
    [
      filterOptions?.project_manager,
      filterOptions?.state,
      filterOptions?.contact_method,
      filterOptions?.subcontractor
    ]
  )

  const initialValues: ProjectFormData = useMemo(() => {
    if (!project) return {} as ProjectFormData

    return {
      ...project,
      subcontractors: project?.subcontractors_details?.map((s) => s?.id),
      file_list: [],
      deleted_attachment_ids: [],
      customer: project?.customer || project?.temporary_customer,
      project_managers: excludeDeletedAccounts(project?.project_managers)?.map(
        (p) => ({
          id: p.id,
          is_main: p?.is_main
        })
      ),
      sales_manager: excludeDeletedAccounts(project?.sales_manager)?.map(
        (s) => ({
          id: s.id,
          is_main: s?.is_main
        })
      ),
      activeProjectTypeId: project?.project_type?.[0]
    }
  }, [project])

  const handleUpdateProject = async ({
    project_type_categories,
    ...data
  }: ProjectFormData) => {
    // Diff against project_type_details (kept fresh by the add/remove reducers),
    // not project_type (only refreshed on a full details PATCH) — otherwise a
    // retry after a mid-sequence failure re-adds an already-assigned type.
    const { addedIds, removedIds } = diffProjectTypeSelection(
      project?.project_type_details?.map((t) => t.id),
      data?.project_type
    )

    const persist = async () => {
      setSubmitting(true)
      try {
        // Add before remove so the backend's "keep at least one type" guard
        // never trips while swapping a project's only type.
        for (const project_type_id of addedIds) {
          await addProjectType({
            projectId: id,
            data: {
              project_type_id,
              category:
                project_type_categories?.[project_type_id] ??
                PROJECT_ENTITY_CATEGORIES.STANDARD
            }
          }).unwrap()
        }

        for (const project_type_id of removedIds) {
          await removeProjectType({ projectId: id, project_type_id }).unwrap()
        }

        const updatedProject = await updateProject({
          id,
          ...data
        } as ProjectUpdateData).unwrap()
        navigate(
          getProjectNavigatePath(
            updatedProject,
            user?.id,
            user?.role?.id,
            projectsViewPath(projectsView)
          )
        )
      } catch (e) {
        console.error("Update project error:", e)
      } finally {
        setSubmitting(false)
      }
    }

    if (removedIds.length) {
      modal.confirm({
        title: "Remove Project Type",
        content: (
          <>
            <p>
              You&apos;re removing{" "}
              {removedIds.length === 1 ? "a project type" : "project types"}{" "}
              from this project.
            </p>
            <p>
              All related milestones, financial items and tasks will be deleted
              as well.
            </p>
          </>
        ),
        okText: "Remove",
        okType: "danger",
        cancelText: "Cancel",
        onOk: persist
      })
      return
    }

    await persist()
  }

  useEffect(() => {
    if (id) getProjectById(id)
  }, [id, getProjectById])

  useEffect(() => {
    generateDocumentTitle("Edit Project")
    fetchFilterOptions()
  }, [])

  if (fetching) return <Skeleton.Node className={"size-full"} />

  if (!project) {
    return (
      <Card size={"small"}>
        <Empty description={"Project not found"} />
      </Card>
    )
  }

  // This route is outside the project layout, so hiding the edit affordances
  // does not stop a bookmark reaching a form the API will refuse to save.
  if (project.is_archived) {
    return <Navigate replace to={RoutesPaths.project.view(id).overview} />
  }

  return (
    <FixedContainer>
      <div className={"grid"}>
        <Flex
          vertical
          gap={"small"}
          className={"max-h-[calc(100vh-90px)] overflow-auto"}
        >
          <Flex justify={"space-between"} align={"center"}>
            <Title level={2} className={"text-start"}>
              Edit Project
            </Title>
            <Link to={RoutesPaths.project.view(id).overview}>
              <Button type={"primary"} icon={<EyeOutlined />}>
                View Project
              </Button>
            </Link>
          </Flex>
          <ProjectForm
            isEdit
            saving={saving || submitting}
            options={options}
            initialValues={initialValues}
            projectFull={project}
            onSubmit={handleUpdateProject}
          />
        </Flex>
      </div>
    </FixedContainer>
  )
}

const mapStateToProps = (state: RootState) => ({
  user: state.auth?.userInfo,
  fetching: state.project.fetching || state.projects.fetchingFilterOptions,
  project: state.project.project,
  saving: state.project.saveFetching,
  filterOptions: state.projects.filterOptions
})

const mapDispatchToProps = {
  getProjectById: fetchProject,
  updateProject,
  addProjectType: addProjectTypeToProject,
  removeProjectType: removeProjectTypeToProject,
  fetchFilterOptions: fetchProjectsFilterOptions
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ProjectEditPage)
